import React, { createElement, Fragment, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import Sentry from '../../utils/sentry';
import Typesense from 'typesense';
import styled, { createGlobalStyle } from 'styled-components';
import { autocomplete } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import '@algolia/autocomplete-theme-classic';
import ProductResult from './product-result-ts';
import RecentSearch from '../../templates/header-search/recent-search';
import configJson from '../typesense-ui/constants.json';

const getConfig = () => ({
  apiKey: configJson.apiKey,
  hostName: configJson.hostName,
  port: configJson.port || 443,
  protocol: configJson.protocol || 'https',
  collection: configJson.collection,
});

const HeaderSearchTypesense = () => {
  const panelRootRef = useRef(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // const container =
    //   window.innerWidth <= 767
    //     ? document.querySelector('#header-search-mobile')
    //     : document.querySelector('#header-search');

    const config = getConfig();
    const typesenseClient = new Typesense.Client({
      nearestNode: {
        host: `${config.hostName}.a1.typesense.net`,
        port: config.port,
        protocol: config.protocol,
      },
      nodes: [
        {
          host: `${config.hostName}-1.a1.typesense.net`,
          port: config.port,
          protocol: config.protocol,
        },
        {
          host: `${config.hostName}-2.a1.typesense.net`,
          port: config.port,
          protocol: config.protocol,
        },
        {
          host: `${config.hostName}-3.a1.typesense.net`,
          port: config.port,
          protocol: config.protocol,
        },
      ],
      apiKey: config.apiKey,
      connectionTimeoutSeconds: 5,
    });

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'typesense-recent',
      limit: 3,
      transformSource({ source, onRemove }) {
        return {
          ...source,
          getItemInputValue: ({ item }) => item.label,
          templates: {
            header: () => <SectionHeader>Recent Searches</SectionHeader>,
            item({ item }) {
              const enrichedItem = { ...item, id: item.label };
              return (
                <RecentSearch
                  hit={enrichedItem}
                  onRemove={onRemove}
                  baseUrl={`${window?.HGR?.homeUrl}?query=`}
                />
              );
            },
            noResults: () => (
              <NoResultsMsg>No recent searches yet.</NoResultsMsg>
            ),
          },
        };
      },
    });

    const search = autocomplete({
      container: '#header-search',
      plugins: [recentSearchesPlugin],
      openOnFocus: true,
      placeholder: 'What are you looking for today?',
      detachedMediaQuery: '(max-width: 767px)', // disables pop-out/modal behavior even on mobile
      renderer: { createElement, Fragment }, // allow React rendering only
      onSubmit: ({ state }) => {
        if (state.query.trim()) {
          window.location.href = `${
            window?.HGR?.homeUrl
          }?query=${encodeURIComponent(state.query)}`;
        }
      },
      onFocus({ setIsOpen }) {
        setTimeout(() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }, 100);
      },
      onStateChange({ state }) {
        sessionStorage.setItem(
          'search_query',
          JSON.stringify(state.query || ''),
        );
        // if (!inputRef.current) {
        //   inputRef.current = container.querySelector('.aa-Input');
        //   window.headerSearchInputRef = inputRef.current;
        // }
      },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
      getSources: ({ query }) => [
        {
          sourceId: 'products',
          getItems() {
            return typesenseClient
              .collections(config.collection)
              .documents()
              .search({
                q: query,
                query_by: 'title,manufacturer,model_num,sku,clean_sku',
                infix: 'off,off,off,always,always',
                per_page: 5,
              })
              .then((res) => res.hits.map((hit) => hit.document))
              .catch((e) => {
                e.name = 'Typesense Header Search Error: ' + e?.name;
                Sentry.captureException(e);
                return [];
              });
          },
          templates: {
            header: () => <SectionHeader>Products</SectionHeader>,
            item: ({ item }) => <ProductResult hit={item} />,
          },
        },
      ],
    });

    return () => {
      search.destroy();
    };
  }, []);

  return (
    <>
      <div id="header-search" data-template="typesense-header-search" />
      <GlobalStyle />
    </>
  );
};

const SectionHeader = styled.h2`
  padding-left: 4px;
  font-size: 16px;
  font-weight: normal;
  color: #777;
`;

const NoResultsMsg = styled.div`
  padding: 10px;
  color: #999;
  font-size: 14px;
  font-style: italic;
`;

const GlobalStyle = createGlobalStyle`
  [data-template="typesense-header-search"] {
    width: 100%;
  }

  .aa-Form {
    flex-direction: row-reverse;
    border-radius: 1px;

    &:focus-within {
      border-color: #1E2A7A;
      box-shadow: none;
      outline: none;
    }
  }

  .aa-InputWrapperPrefix {
    order: 1;
  }

  .aa-InputWrapper {
    order: 3;
  }

  .aa-InputWrapperSuffix {
    order: 2;
  }

  .aa-Input {
    padding-left: 10px;
  }

  .aa-Label {
    margin-bottom: 0;
  }

  .aa-SubmitButton {
    background: #1E2A7A;
    height: calc(100% + 2px);
    margin: -1px -1px 0 0;

    > svg {
      color: #fff;
    }
  }

  .aa-Panel {
    z-index: 1000;
  }

  .aa-Item {
    border-radius: 0;

    &[aria-selected="true"] {
      background-color: #eee;
    }
  }

  .aa-DetachedSearchButton {
    padding-right: 0;
    border-radius: 1px;
  }

  .aa-DetachedSearchButtonIcon {
    order: 3;
    margin-left: auto;
    background: #1E2A7A;
    width: 42px;
    height: 42px;
    color: #fff;
  }
`;

export default HeaderSearchTypesense;
